import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { submitPrivateInquiry } from "@/lib/api";
import { toast } from "sonner";

const TITLES = {
  viewing: "Request Private Viewing",
  pricing: "Request Pricing",
  appointment: "Private Appointment",
};

const SUBTITLES = {
  viewing: "An encounter, held in confidence.",
  pricing: "Pricing is shared in private correspondence.",
  appointment: "Reserve time with a maison concierge.",
};

export default function PrivateInquiryDialog({ open, type, piece, onClose }) {
  const [form, setForm] = useState({ full_name: "", email: "", country: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm({ full_name: "", email: "", country: "", notes: "" });
  }, [open, type]);

  if (!open) return null;

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error("Please share your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await submitPrivateInquiry({
        inquiry_type: type,
        full_name: form.full_name,
        email: form.email,
        country: form.country || null,
        piece: piece || null,
        notes: form.notes || null,
      });
      toast.success("Thank you for your inquiry. A PiérrecDavís concierge will respond personally within 72 hours.");
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Unable to submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Unable to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="private-inquiry-dialog"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
    >
      <div
        className="absolute inset-0 bg-black/85 backdrop-blur-md"
        onClick={onClose}
        data-testid="private-inquiry-backdrop"
      />
      <div className="relative w-full max-w-2xl bg-black border border-[var(--pd-border)] p-8 md:p-12">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-white/60 hover:text-[var(--pd-gold)] transition-colors"
          data-testid="private-inquiry-close"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <p className="eyebrow" data-testid="private-inquiry-eyebrow">
          {piece ? `${piece} · Concierge` : "Concierge"}
        </p>
        <h3 className="mt-4 font-serif-display text-3xl md:text-4xl text-white tracking-[0.06em]" data-testid="private-inquiry-title">
          {TITLES[type] || "Private Inquiry"}
        </h3>
        <p className="mt-2 text-white/60 text-sm">
          {SUBTITLES[type] || "Begin a private conversation with the maison."}
        </p>

        <form onSubmit={handleSubmit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
          <label className="block">
            <span className="block eyebrow mb-2">Full Name</span>
            <input className="pd-input" value={form.full_name} onChange={update("full_name")} required data-testid="private-inquiry-name" />
          </label>
          <label className="block">
            <span className="block eyebrow mb-2">Email</span>
            <input type="email" className="pd-input" value={form.email} onChange={update("email")} required data-testid="private-inquiry-email" />
          </label>
          <label className="block md:col-span-2">
            <span className="block eyebrow mb-2">Country</span>
            <input className="pd-input" value={form.country} onChange={update("country")} data-testid="private-inquiry-country" />
          </label>
          <label className="block md:col-span-2">
            <span className="block eyebrow mb-2">Notes (optional)</span>
            <textarea rows={3} className="pd-input resize-none" value={form.notes} onChange={update("notes")} data-testid="private-inquiry-notes" />
          </label>

          <div className="md:col-span-2 flex items-center justify-end gap-4 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost" data-testid="private-inquiry-cancel">
              Cancel
            </button>
            <button type="submit" disabled={submitting} className="btn-gold disabled:opacity-60" data-testid="private-inquiry-submit">
              <span>{submitting ? "Sending…" : "Send Request"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
